import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronRight, Clock } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { getArticle, readingTime } from "@/data/articles";
import { useDocumentMeta } from "@/lib/seo";

/* --------------------------- rendu des blocs --------------------------- */

const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="mt-12 font-display text-2xl font-medium leading-snug tracking-tight text-navy sm:mt-14 sm:text-3xl">
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="mt-8 font-display text-xl font-medium leading-snug text-navy sm:text-2xl">
          {block.text}
        </h3>
      );
    case "p":
      return <p className="mt-5 text-sm leading-relaxed text-stone-600 sm:text-base">{block.text}</p>;
    case "list":
      return (
        <ul className="mt-6 space-y-3">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3.5 text-sm leading-relaxed text-stone-600 sm:text-base">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cognac" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "callout":
      return (
        <aside data-testid="article-callout" className="mt-10 rounded-2xl border-l-4 border-cognac bg-cream px-6 py-6 sm:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-cognac">{block.title}</p>
          <p className="mt-3 font-display text-lg font-medium italic leading-relaxed text-navy sm:text-xl">
            {block.text}
          </p>
        </aside>
      );
    case "steps":
      return (
        <ol data-testid="article-steps" className="mt-10 space-y-0">
          {block.items.map((s, i) => (
            <li key={s.title} className="relative flex gap-5 pb-8 last:pb-0">
              {i < block.items.length - 1 && (
                <span className="absolute left-[19px] top-11 h-[calc(100%-2.75rem)] w-px bg-[#E4DFD5]" aria-hidden="true" />
              )}
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sage/12 font-display text-sm font-medium text-sage">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="pt-1">
                <p className="text-sm font-bold text-navy sm:text-base">{s.title}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-stone-600">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>
      );
    default:
      return null;
  }
};

/* --------------------------------- page --------------------------------- */

export default function ArticlePage() {
  const { slug } = useParams();
  const article = getArticle(slug);

  useDocumentMeta(
    article ? `${article.title} — Conseils & inspirations | CozyHome` : "Conseils & inspirations | CozyHome",
    article?.metaDescription
  );

  if (!article) return <Navigate to="/conseils" replace />;

  const minutes = readingTime(article);
  const related = article.related.map(getArticle).filter(Boolean);

  return (
    <PageShell>
      <article data-testid="article-page" className="bg-ivory pb-20 pt-28 sm:pt-36">
        {/* En-tête éditorial */}
        <header className="mx-auto max-w-3xl px-5 sm:px-8">
          <nav data-testid="article-breadcrumb" aria-label="Fil d'Ariane" className="flex items-center gap-1.5 text-xs text-stone-400">
            <Link to="/conseils" className="font-semibold transition-colors hover:text-cognac">
              Conseils &amp; inspirations
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-stone-500">{article.category}</span>
          </nav>
          <p data-testid="article-category" className="mt-8 inline-block rounded-full bg-sage/12 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-sage">
            {article.category}
          </p>
          <h1 className="mt-5 font-display text-4xl font-medium leading-[1.1] tracking-tight text-navy sm:text-5xl">
            {article.title}
          </h1>
          <p className="mt-6 font-display text-lg font-normal italic leading-relaxed text-stone-500 sm:text-xl">
            {article.intro}
          </p>
          <p data-testid="article-reading-time" className="mt-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-400">
            <Clock className="h-3.5 w-3.5" /> {minutes} min de lecture
          </p>
        </header>

        {/* Image principale */}
        <div className="mx-auto mt-10 max-w-4xl px-5 sm:px-8">
          <div className="overflow-hidden rounded-3xl">
            <img
              src={article.img}
              alt={article.imgAlt}
              className="aspect-[16/9] w-full object-cover"
            />
          </div>
        </div>

        {/* Corps de l'article */}
        <div data-testid="article-body" className="mx-auto max-w-3xl px-5 sm:px-8">
          <div className="mt-10">
            {article.blocks.map((b, i) => (
              <Block key={i} block={b} />
            ))}
          </div>

          {/* Conclusion */}
          <div data-testid="article-conclusion" className="mt-14 rounded-3xl bg-navy px-7 py-9 sm:px-10 sm:py-11">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-wood">Pour conclure</p>
            <p className="mt-4 font-display text-xl font-medium italic leading-relaxed text-white sm:text-2xl">
              {article.conclusion}
            </p>
          </div>
        </div>

        {/* Passerelles */}
        <div className="mx-auto mt-16 max-w-5xl px-5 sm:px-8 sm:mt-20">
          <Reveal>
            <h2 data-testid="article-related-title" className="font-display text-2xl font-medium tracking-tight text-navy sm:text-3xl">
              À lire aussi
            </h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r, i) => (
              <Reveal key={r.slug} delay={i * 0.08}>
                <Link
                  to={`/conseils/${r.slug}`}
                  data-testid={`article-related-${i}`}
                  className="group block"
                >
                  <div className="overflow-hidden rounded-3xl">
                    <img
                      src={r.img}
                      alt={r.imgAlt}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-5 px-1">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-cognac">{r.category}</p>
                    <p className="mt-2 font-display text-xl font-medium leading-snug text-navy transition-colors duration-300 group-hover:text-sage">
                      {r.title}
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div data-testid="article-cta" className="mt-16 rounded-3xl bg-cream p-8 text-center sm:p-12">
              <h2 className="font-display text-2xl font-medium leading-snug text-navy sm:text-3xl">
                Votre projet commence à <span className="italic text-cognac">prendre forme ?</span>
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-stone-500 sm:text-base">
                Quelques informations suffisent pour nous parler de votre projet et
                commencer à structurer la suite.
              </p>
              <Link
                to="/estimer"
                data-testid="article-cta-estimer"
                className="group mt-7 inline-flex items-center gap-2.5 rounded-full bg-navy px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-sage"
              >
                Parler de mon projet
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>

          <div className="mt-12">
            <Link
              to="/conseils"
              data-testid="article-back"
              className="inline-flex items-center gap-2 text-sm font-bold text-navy/60 transition-colors duration-200 hover:text-cognac"
            >
              <ArrowLeft className="h-4 w-4" /> Tous les conseils &amp; inspirations
            </Link>
          </div>
        </div>
      </article>
    </PageShell>
  );
}
